import { useState, useEffect, useCallback, RefObject } from "react"

import { IMultipleEntities, PaginationMetadata } from "@/types"
import CustomError from "models/CustomError"
import { ErrorCode } from "config/constants"

export default function usePagedData<TResponseItem, TResponseAggregates = {}>(
    initCall?: () => Promise<IMultipleEntities<TResponseItem, TResponseAggregates>>,
    onFail?: () => void,
    onFetched?: (data: IMultipleEntities<TResponseItem, TResponseAggregates>, isFirstPage?: boolean) => void | Promise<void>,
    { cancelToken, autoload = true }: { cancelToken?: RefObject<AbortController | null>, autoload?: boolean } = {}
) {
    const [items, setItems] = useState<TResponseItem[] | null>(null)
    const [aggregates, setAggregates] = useState<TResponseAggregates | null>(null)
    const [pagination, setPagination] = useState<PaginationMetadata | null>(null)
    const [loadNextPageCall, setLoadNextPageCall] = useState<(() => Promise<IMultipleEntities<TResponseItem, TResponseAggregates> | null>) | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isInitialLoad, setIsInitialLoad] = useState(true)

    const hasMore = !!loadNextPageCall

    const handleFail = useCallback(() => {
        if (typeof onFail === "function") {
            onFail()
        }
    }, [onFail])

    const fetchData = useCallback(async () => {
        if (!initCall) return
        setIsLoading(true)
        setLoadNextPageCall(null)
        setItems(null)
        cancelToken?.current?.abort()
        try {
            const result = await initCall()
            if (!result) {
                setIsLoading(false)
                handleFail()
                return
            }
            await onFetched?.(result, true)
            setIsLoading(false)
            setItems(result.list || null)
            setAggregates(result.aggregates || null)
            setPagination(result.pagination || null)
            setLoadNextPageCall(result.loadMore ? () => result.loadMore! : null)
            setIsInitialLoad(false)
        } catch (e) {
            if (e instanceof CustomError && e.code === ErrorCode.requestCanceled) {
                return
            }
            handleFail()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initCall, handleFail])

    const loadNextPage = useCallback(async () => {
        setLoadNextPageCall(null)
        if (!loadNextPageCall) return
        setIsLoading(true)
        try {
            const result = await loadNextPageCall()
            if (!result) {
                setIsLoading(false)
                handleFail()
                return
            }
            await onFetched?.(result)
            setIsLoading(false)
            setPagination(result.pagination || null)
            setItems([...items || [], ...result.list || []])
            setLoadNextPageCall(result.loadMore ? () => result.loadMore! : null)
        } catch (e) {
            if (e instanceof CustomError && e.code === ErrorCode.requestCanceled) {
                return
            }
            handleFail()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleFail, items, loadNextPageCall])

    useEffect(() => {
        if (!autoload) return
        fetchData()
    }, [autoload, fetchData])

    return {
        items,
        aggregate: aggregates,
        isInitialLoad,
        hasMore,
        isLoading,
        loadNextPage,
        pagination,
        refetchData: fetchData,
        setItems,
        setAggregates,
    }
}
